import React, { useState, useEffect } from 'react';
import { notify } from './ui/notify';
import { apiCall } from './config';
import { APP_VERSION, getBuildInfo } from './version';
import UserManagement from './UserManagement';
import FlexibleShiftPlanning from './FlexibleShiftPlanning';
import UserAvailability from './UserAvailability';
import DocumentManagement from './DocumentManagement';
import PersonalPlanningEnhanced from './components/MobileShiftPlanning'; // am Handy Listen-Ansicht, sonst die große Ansicht
import ActualHoursTracking from './components/ActualHoursTracking';
import EmployeeWorkReports from './components/EmployeeWorkReports';
import GeneralDocuments from './components/GeneralDocuments';
import UserProfile from './components/UserProfile';
import WheelchairAdmin from './WheelchairAdmin';
import WgReservations from './components/WgReservations';
import IdentityCheck from './components/IdentityCheck';
import CashPickups from './components/CashPickups';
import BackupManager from './BackupManager';
import TasksAndPlanning from './components/TasksAndPlanning';
import ClaimDeclineModal from './components/ClaimDeclineModal';
import WorkInstructions from './WorkInstructions';
import LuggageShippingAdmin from './components/LuggageShippingAdmin';
import LuggageCases from './components/LuggageCases';
import LuggageClaimsAdmin from './components/LuggageClaimsAdmin';
import WhatsAppInbox from './WhatsAppInbox';
import { getStationName } from './utils/stationMapping';
import { useTranslation, LANGUAGES } from './i18n/LanguageContext';

function Dashboard({ user, onLogout }) {
  // Rolle „Gepäck“ (z.B. Philipps Vater) sieht nur den Gepäck-Reiter
  const [activeTab, setActiveTab] = useState(user && user.role === 'luggage' ? 'luggageShipping' : 'overview');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [langMenuStyle, setLangMenuStyle] = useState(null);
  const [currentUser, setCurrentUser] = useState(user);

  // Deep-Link aus WhatsApp: ?open=aufgaben[&task=30] springt direkt ins
  // Aufgaben-Board (nur Admin) und öffnet dort ggf. die verlinkte Karte.
  const [deepTaskId, setDeepTaskId] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const open = (params.get('open') || '').toLowerCase();
    if (open === 'aufgaben' && user.role === 'admin') {
      setActiveTab('tasksAndPlanning');
      if (params.get('task')) setDeepTaskId(Number(params.get('task')));
      // Parameter entfernen, damit Reload/Navigation nicht erneut springt
      window.history.replaceState({}, '', window.location.pathname);
    }
    // ?open=gepaeck → Gepäck & Vorgänge (Link aus der WhatsApp-Vorlage „neuer Vorgang“)
    if (open === 'gepaeck' && ['admin', 'luggage'].includes(user.role)) {
      setActiveTab('luggageShipping');
      window.history.replaceState({}, '', window.location.pathname);
    }
    // ?open=identitaet → direkt in die digitale Identitätsprüfung (Link aus der WhatsApp-Vorlage)
    if (open === 'identitaet') {
      setActiveTab('identityCheck');
      window.history.replaceState({}, '', window.location.pathname);
    }
    // ?open=einsatz → direkt zu „Einsatz" (Stunden bestätigen, Link aus der WhatsApp-Vorlage nach der Wiesn)
    if (open === 'einsatz' || open === 'feedback') {
      // „feedback": Formular unter „Einsatz" aufklappen und anspringen (FeedbackPanel liest den Merker)
      if (open === 'feedback') {
        try { sessionStorage.setItem('openFeedback', '1'); } catch (_) { /* egal */ }
        // falls „Einsatz" schon geladen ist: Formular per Signal öffnen
        setTimeout(() => window.dispatchEvent(new Event('open-feedback')), 400);
      }
      setActiveTab('reports');
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sprach-Menü an der Flaggen-Position öffnen – als position:fixed, damit es auf
  // Mobile nicht abgeschnitten wird oder off-screen landet.
  const toggleLangMenu = (e) => {
    e.stopPropagation();
    if (showLangMenu) { setShowLangMenu(false); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const openUp = r.top > window.innerHeight / 2; // Flagge unten → Menü nach oben
    setLangMenuStyle({
      position: 'fixed',
      right: Math.max(8, Math.round(window.innerWidth - r.right)),
      zIndex: 3000,
      minWidth: 170,
      maxHeight: '70vh',
      overflowY: 'auto',
      ...(openUp
        ? { bottom: Math.round(window.innerHeight - r.top + 4) }
        : { top: Math.round(r.bottom + 4) }),
    });
    setShowLangMenu(true);
  };
  const { t, language, setLanguage } = useTranslation();

  // Ungelesene WhatsApp-Nachrichten fürs Menü-Badge (nur Admin; alle 60s + beim Tab-Wechsel)
  const [waUnread, setWaUnread] = useState(0);
  useEffect(() => {
    if (currentUser.role !== 'admin') return;
    let alive = true;
    const load = async () => {
      try {
        const d = await apiCall('/admin/wa/unread-count');
        if (alive) setWaUnread(d.count || 0);
      } catch { /* Badge ist nice-to-have – Fehler still ignorieren */ }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, [currentUser.role, activeTab]);

  // admin: nur Admins. lead: zusätzlich für die Einsatzleitung (role 'lead') –
  // sie sieht die Mitarbeiter-Tabs plus die Dienstplanung (bewusst OHNE
  // Nutzerverwaltung, Aufgaben-Board, Stunden, WhatsApp-Postfach, Backups).
  const tabs = [
    { id: 'overview', name: t('tab.overview'), icon: 'bi-house-fill', admin: false },
    { id: 'users', name: t('tab.users'), icon: 'bi-people-fill', admin: true },
    { id: 'personalPlanning', name: t('tab.personalPlanning'), icon: 'bi-diagram-3-fill', admin: true, lead: true, planner: true },
    { id: 'flexibleShifts', name: t('tab.flexibleShifts'), icon: 'bi-calendar-range', admin: true, lead: true, planner: true },
    { id: 'tasksAndPlanning', name: t('tab.tasksAndPlanning'), icon: 'bi-list-task', admin: true },
    { id: 'availability', name: t('tab.availability'), icon: 'bi-calendar-check', admin: false },
    { id: 'documents', name: t('tab.documents'), icon: 'bi-file-earmark-text-fill', admin: false },
    { id: 'actualHours', name: t('tab.actualHours'), icon: 'bi-clock-history', admin: true },
    { id: 'reports', name: t('tab.reports'), icon: 'bi-graph-up', admin: false },
    { id: 'luggageShipping', name: t('tab.luggageShipping'), icon: 'bi-box-seam', admin: true },
    { id: 'whatsappInbox', name: t('tab.whatsappInbox'), icon: 'bi-whatsapp', admin: true },
    { id: 'wheelchairAdmin', name: t('tab.wheelchairAdmin'), icon: 'bi-wheelchair', admin: false },
    // Digitale Identitätsprüfung bei verlorener Garderobenmarke – für ALLE Mitarbeiter (ersetzt den Papierzettel)
    { id: 'identityCheck', name: 'Identitätsprüfung', icon: 'bi-person-vcard', admin: false },
    // Tischreservierungen Wiesn Garten: Leitung, Einsatzleitung, WG-Planer + alle WG-Freigeschalteten
    { id: 'wgReservations', name: 'Tischreservierungen', icon: 'bi-calendar2-check', admin: true, lead: true, planner: true, wg: true },
    // Geldabholung: nur Admins (Philipp + Daniel); Inhalte werden im Browser verschlüsselt
    { id: 'cashPickups', name: 'Geldabholung', icon: 'bi-safe', admin: true },
    { id: 'backups', name: t('tab.backups'), icon: 'bi-shield-check', admin: true }
  ];

  const filteredTabs = currentUser.role === 'luggage' ? tabs.filter((tab) => tab.id === 'luggageShipping') : tabs.filter(tab =>
    !tab.admin || currentUser.role === 'admin'
    || (tab.lead && currentUser.role === 'lead')
    || (tab.planner && currentUser.role === 'planner')
    || (tab.wg && currentUser.can_work_wiesngarten)
  );

  // Klick außerhalb des Menüs schließt es
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
      if (showLangMenu && !event.target.closest('.lang-menu-container')) {
        setShowLangMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showUserMenu, showLangMenu]);

  const handleUserUpdate = (updatedUser) => {
    setCurrentUser(updatedUser);
  };

  const handleShowProfile = () => {
    setShowUserMenu(false);
    setShowProfile(true);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <Overview user={currentUser} onGoTab={setActiveTab} />;
      case 'users':
        return <UserManagement user={currentUser} />;
      case 'personalPlanning':
        return <PersonalPlanningEnhanced user={currentUser} />;
      case 'flexibleShifts':
        return <FlexibleShiftPlanning user={currentUser} />;
      case 'tasksAndPlanning':
        return (
          <TasksAndPlanning
            user={currentUser}
            initialTaskId={deepTaskId}
            onDeepTaskConsumed={() => setDeepTaskId(null)}
          />
        );
      case 'availability':
        return <UserAvailability user={currentUser} />;
      case 'documents':
        return <DocumentManagement user={currentUser} />;
      case 'actualHours':
        return <ActualHoursTracking user={currentUser} />;
      case 'reports':
        return <EmployeeWorkReports user={currentUser} />;
      case 'luggageShipping':
        return <><LuggageCases user={currentUser} /><LuggageClaimsAdmin /><LuggageShippingAdmin user={currentUser} /></>;
      case 'whatsappInbox':
        return <WhatsAppInbox user={currentUser} />;
      case 'identityCheck':
        return <IdentityCheck />;
      case 'cashPickups':
        return currentUser.role === 'admin' ? <CashPickups user={currentUser} /> : null;
      case 'wgReservations':
        return <WgReservations />;
      case 'wheelchairAdmin':
        if (currentUser.role !== 'admin' && !currentUser.approved) {
          return (
            <div className="container py-5">
              <div className="row justify-content-center">
                <div className="col-md-6">
                  <div className="card border-warning shadow">
                    <div className="card-body text-center p-5">
                      <i className="bi bi-hourglass-split text-warning" style={{fontSize: '4rem'}}></i>
                      <h4 className="mt-3 mb-3">{t('approval.title')}</h4>
                      <p className="text-muted">
                        {t('approval.description')}
                      </p>
                      <span className="badge bg-warning text-dark fs-6">
                        <i className="bi bi-lock-fill me-2"></i>{t('approval.badge')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        return <WheelchairAdmin user={currentUser} />;
      case 'backups':
        return <BackupManager user={currentUser} />;
      default:
        return <Overview user={currentUser} onGoTab={setActiveTab} />;
    }
  };

  const isEmployee = currentUser.role !== 'admin';

  return (
    <div className="min-vh-100" style={{backgroundColor: '#f8f9fa'}}>
      {/* Gemeinsames Sprach-Menü (position:fixed → nie abgeschnitten/off-screen) */}
      {showLangMenu && langMenuStyle && (
        <div className="dropdown-menu show lang-menu-container" style={langMenuStyle}>
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              type="button"
              className={`dropdown-item ${language === lang.code ? 'active' : ''}`}
              onClick={() => { setLanguage(lang.code); setShowLangMenu(false); }}
            >
              <span className="me-2">{lang.flag}</span>{lang.name}
            </button>
          ))}
        </div>
      )}
      {/* Navigation Bar - auf Mobilgeräten für Mitarbeiter ausgeblendet */}
      <nav className={`navbar navbar-expand-lg navbar-dark sticky-top ${isEmployee ? 'd-none d-lg-flex' : ''}`} style={{backgroundColor: '#2c3e50'}}>
        <div className="container-fluid">
          <span className="navbar-brand">
            <i className="bi bi-cup-hot-fill text-warning me-2"></i>
            <span className="d-none d-md-inline">{t('nav.brand')}</span>
            <span className="d-md-none">Personalplanung</span>
          </span>
          
          {/* Toggle Button für mobile Geräte - nur für Admin */}
          {!isEmployee && (
            <button 
              className="navbar-toggler" 
              type="button" 
              data-bs-toggle="collapse" 
              data-bs-target="#navbarNav"
              aria-controls="navbarNav" 
              aria-expanded="false" 
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon"></span>
            </button>
          )}

          {/* Desktop Navigation für alle, Mobile nur für Admin */}
          <div className={`${isEmployee ? 'd-none d-lg-flex' : 'collapse'} navbar-collapse`} id="navbarNav">
            <div className="navbar-nav mx-auto">
              {filteredTabs.map(tab => (
                <button
                  key={tab.id}
                  className={`nav-link btn btn-link text-white border-0 mx-1 ${
                    activeTab === tab.id ? 'fw-bold text-warning' : ''
                  }`}
                  onClick={() => {
                    setActiveTab(tab.id);
                    // Mobile menu schließen nach Auswahl
                    const navElement = document.querySelector('#navbarNav');
                    const navToggler = document.querySelector('.navbar-toggler');
                    if (navElement && navToggler && !navToggler.classList.contains('collapsed')) {
                      navElement.classList.remove('show');
                    }
                  }}
                  style={{ 
                    textDecoration: 'none',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    backgroundColor: activeTab === tab.id ? 'rgba(255, 193, 7, 0.1)' : 'transparent'
                  }}
                >
                  <i className={`${tab.icon} me-1`}></i>
                  <span className="d-none d-md-inline">{tab.name}</span>
                  <span className="d-md-none">{tab.name.substring(0, 8)}</span>
                  {tab.id === 'whatsappInbox' && waUnread > 0 && (
                    <span className="badge rounded-pill bg-danger ms-1" style={{ fontSize: '10px' }}>{waUnread}</span>
                  )}
                </button>
              ))}
            </div>
          
          {/* Language Switcher + User-Menü rechts */}
          <div className="navbar-nav d-flex flex-row align-items-center">
            {/* Language Switcher – kompakte Flagge, Menü als position:fixed */}
            <div className="nav-item lang-menu-container me-2">
              <button
                type="button"
                className="nav-link btn btn-link text-white border-0"
                onClick={toggleLangMenu}
                style={{ textDecoration: 'none', fontSize: '1.3rem', touchAction: 'manipulation' }}
                title={t('nav.language')}
                aria-label={t('nav.language')}
              >
                {LANGUAGES.find(l => l.code === language)?.flag || '🇩🇪'}
              </button>
            </div>
            <div className="nav-item user-menu-container position-relative">
              <button
                className="nav-link btn btn-link text-white border-0 d-flex align-items-center"
                onClick={() => setShowUserMenu(!showUserMenu)}
                style={{ textDecoration: 'none' }}
              >
                <i className="bi bi-person-circle me-2"></i>
                <span className="d-none d-md-inline">
                  {currentUser.firstname && currentUser.lastname
                    ? `${currentUser.firstname} ${currentUser.lastname}`
                    : currentUser.name || t('nav.user')}
                </span>
                <span className="d-md-none">
                  {currentUser.firstname && currentUser.lastname 
                    ? currentUser.firstname 
                    : 'User'}
                </span>
                <i className={`bi bi-chevron-${showUserMenu ? 'up' : 'down'} ms-2`}></i>
              </button>
              
              {showUserMenu && (
                <div 
                  className="dropdown-menu position-absolute show"
                  style={{ 
                    right: 0, 
                    top: '100%', 
                    minWidth: '200px',
                    zIndex: 1050 
                  }}
                >
                  <button className="dropdown-item" onClick={handleShowProfile}>
                    <i className="bi bi-person-fill me-2"></i>{t('nav.profile')}
                  </button>
                  <hr className="dropdown-divider" />
                  <button 
                    className="dropdown-item" 
                    onClick={() => {
                      setShowUserMenu(false);
                      onLogout();
                    }}
                  >
                    <i className="bi bi-box-arrow-right me-2"></i>{t('nav.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </nav>

      {/* Mobile Tab-Leiste für Mitarbeiter (nur auf kleinen Bildschirmen) */}
      {isEmployee && (
        <div className="d-lg-none" style={{
          backgroundColor: '#2c3e50',
          borderBottom: '2px solid #1a252f',
          position: 'sticky',
          top: 0,
          zIndex: 1030,
          display: 'flex',
          alignItems: 'stretch'
        }}>
          {/* Scrollable Tabs */}
          <div style={{ flex: '1 1 auto', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <div className="d-flex" style={{ minWidth: 'fit-content' }}>
              {filteredTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="btn btn-link border-0 text-center px-2 py-2"
                  style={{
                    textDecoration: 'none',
                    color: activeTab === tab.id ? '#f1c40f' : 'rgba(255,255,255,0.7)',
                    borderBottom: activeTab === tab.id ? '3px solid #f1c40f' : '3px solid transparent',
                    borderRadius: 0,
                    minWidth: '55px',
                    flex: '1 0 auto',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <i className={`${tab.icon} d-block`} style={{ fontSize: '1.2rem' }}></i>
                  <small style={{ fontSize: '0.6rem', whiteSpace: 'pre-line', lineHeight: '1.1' }}>{tab.name}</small>
                </button>
              ))}
            </div>
          </div>
          {/* Fixed Language Switcher - always visible (Flagge, Menü als position:fixed) */}
          <div className="lang-menu-container d-flex align-items-center" style={{ flex: '0 0 auto', borderLeft: '1px solid rgba(255,255,255,0.2)' }}>
            <button
              type="button"
              onClick={toggleLangMenu}
              className="btn btn-link border-0 text-center px-3 py-2"
              style={{ textDecoration: 'none', color: 'rgba(255,255,255,0.9)', borderRadius: 0, fontSize: '1.4rem', minWidth: '48px', touchAction: 'manipulation' }}
              aria-label={t('nav.language')}
            >
              {LANGUAGES.find(l => l.code === language)?.flag || '🇩🇪'}
            </button>
          </div>
        </div>
      )}

      {/* Main Content - Vollbreite ohne Sidebar */}
      <div className="container-fluid p-4">
        {renderContent()}
      </div>

      {/* Version Footer */}
      <footer className="mt-auto py-2 px-3" style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #dee2e6' }}>
        <div className="container-fluid">
          <div className="row align-items-center">
            <div className="col-md-6">
              <small className="text-muted">
                <i className="bi bi-info-circle me-1"></i>
                {getBuildInfo()}
              </small>
            </div>
            <div className="col-md-6 text-end">
              <small className="text-muted">
                Features: {APP_VERSION.features.slice(0, 2).join(' • ')}
              </small>
            </div>
          </div>
        </div>
      </footer>

      {/* User Profile Modal */}
      {showProfile && (
        <UserProfile
          user={currentUser}
          onClose={() => setShowProfile(false)}
          onUserUpdate={handleUserUpdate}
        />
      )}
    </div>
  );
}

function Overview({ user, onGoTab }) {
  const [todayShifts, setTodayShifts] = useState([]);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [notifications, setNotifications] = useState([]);
  // Solange die Planung nicht final ist (WHATSAPP_SHIFTS_LIVE=false), sehen
  // Mitarbeiter einen großen Hinweis – damit sich niemand über halb geplante
  // Tage wundert. Verschwindet automatisch, sobald Philipp final schaltet.
  const [planningFinal, setPlanningFinal] = useState(true);
  // Schichtbörse: freie Schichten, auf die sich Mitarbeiter melden können
  const [offers, setOffers] = useState([]);
  const [adminClaims, setAdminClaims] = useState([]); // Admin: Meldungen auf freie Schichten
  const [declineBox, setDeclineBox] = useState(null); // { claim, offer, note } – Absage mit Begruendung
  // Einsatzzeiten bestaetigt? Wer kein WhatsApp nutzt, erfaehrt sonst nie davon.
  const [planStatus, setPlanStatus] = useState(null);
  const [myClaims, setMyClaims] = useState([]);   // eigene Anfragen inkl. Ausgang
  const { t } = useTranslation();

  useEffect(() => {
    if (user.role !== 'admin' && user.id) {
      fetchTodayShifts();
      fetchNotifications();
      fetchOffers();
      fetchPlanStatus();
      fetchMyClaims();
      apiCall('/planning-status')
        .then((d) => setPlanningFinal(!!d.final))
        .catch(() => {});
    }
    if (user.role === 'admin') fetchAdminClaims();
  }, [user]);

  const fetchMyClaims = async () => {
    try {
      const d = await apiCall(`/my-shift-claims/${user.id}`);
      setMyClaims(Array.isArray(d) ? d : []);
    } catch (_) { setMyClaims([]); }
  };

  const fetchPlanStatus = async () => {
    try { setPlanStatus(await apiCall(`/my-shiftplan-status/${user.id}`)); } catch (_) { setPlanStatus(null); }
  };

  const replyPlan = async (problem) => {
    try {
      await apiCall(`/my-shiftplan-status/${user.id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ problem }),
      });
      notify(problem ? 'Danke – die Leitung meldet sich bei dir.' : 'Danke, deine Einsatzzeiten sind bestätigt! ✅');
      fetchPlanStatus();
    } catch (_) { notify('Hat nicht geklappt – bitte nochmal versuchen.'); }
  };

  const fetchOffers = async () => {
    try {
      const d = await apiCall(`/shift-offers?user_id=${user.id}`);
      setOffers(Array.isArray(d) ? d : []);
    } catch (_) { setOffers([]); }
  };

  // Admin-Sicht: nur die offenen Schichten, auf die sich jemand gemeldet hat
  const fetchAdminClaims = async () => {
    try {
      const d = await apiCall('/admin/shift-claims');
      setAdminClaims(Array.isArray(d) ? d.filter((o) => o.claims.length) : []);
    } catch (_) { setAdminClaims([]); }
  };

  const decideClaim = async (claim, offer, accept, note = '', notifyEmp = false) => {
    try {
      if (accept) {
        const day = await apiCall(`/planning/assignments/${offer.date}`).catch(() => []);
        const from = offer.from * 60, to = (offer.to === 25 ? 25 : offer.to) * 60;
        const toMin = (t) => { const [h, m] = String(t).split(':').map(Number); return h * 60 + (m || 0); };
        const schonDa = (Array.isArray(day) ? day : []).some((a) => {
          if (Number(a.user_id) !== Number(claim.user_id)) return false;
          let s = toMin(a.start_time); let e = toMin(a.end_time); if (e <= s) e += 1440;
          return Math.min(e, to) - Math.max(s, from) > 0;   // ueberschneidet sich -> schon eingeplant
        });
        if (!schonDa) {
          const res = await apiCall('/planning/assign', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              date: offer.date, user_id: claim.user_id,
              station_id: String(offer.station).toUpperCase() === 'WIESN-GARTEN' ? 'WG' : String(offer.station).toUpperCase(),
              start_time: `${String(offer.from).padStart(2, '0')}:00`,
              end_time: `${String(offer.to === 25 ? 1 : offer.to).padStart(2, '0')}:00`,
              is_cashier: false,
            }),
          });
          if (!res || !res.success) { notify('Einplanen fehlgeschlagen'); return; }
        }
      }
      const r = await apiCall(`/admin/shift-claims/${claim.claim_id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision: accept ? 'assigned' : 'declined', note, notify: !accept && notifyEmp }),
      });
      setDeclineBox(null);
      notify(!accept
        ? (!notifyEmp ? `${claim.name} abgelehnt`
          : r && r.notified ? `${claim.name} abgelehnt – WhatsApp verschickt`
          : `${claim.name} abgelehnt – ⚠️ WhatsApp NICHT verschickt (${(r && r.reason) || 'unbekannt'})`)
        : r && r.notified ? `${claim.name} eingeplant – WhatsApp verschickt (${r.channel === 'template' ? 'Template' : 'Chat'})`
        : `${claim.name} eingeplant – ⚠️ WhatsApp NICHT verschickt (${(r && r.reason) || 'unbekannt'})`);
      fetchAdminClaims();
    } catch (_) { notify('Aktion fehlgeschlagen'); }
  };

  // opts: { withdraw } zurücknehmen · { decline } absagen · {} zusagen
  const claimOffer = async (offerId, opts = {}) => {
    try {
      await apiCall(`/shift-offers/${offerId}/claim`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, ...opts, source: 'web' }),
      });
      notify(opts.withdraw ? 'Zurückgenommen.'
        : opts.decline ? 'Alles klar, notiert – danke für die Rückmeldung.'
        : 'Danke! Deine Anfrage ist notiert – die Leitung meldet sich. 🙌');
      fetchOffers();
      fetchMyClaims();
    } catch (e) {
      notify('Das hat nicht geklappt – die Schicht ist vielleicht schon vergeben.');
    }
  };

  const fetchTodayShifts = async () => {
    setLoadingShifts(true);
    try {
      const shifts = await apiCall(`/employee/today-shifts/${user.id}`);
      setTodayShifts(shifts);
    } catch (error) {
      console.error('Fehler beim Laden der heutigen Schichten:', error);
    } finally {
      setLoadingShifts(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const data = await apiCall(`/employee/notifications/${user.id}`);
      setNotifications(Array.isArray(data?.notifications) ? data.notifications : []);
    } catch (error) {
      console.error('Fehler beim Laden der Benachrichtigungen:', error);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5); // HH:MM aus HH:MM:SS
  };

  const getTodaysDate = () => {
    return new Date().toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2><i className="bi bi-house-fill me-2 text-primary"></i>{t('overview.title')}</h2>
        <small className="text-muted">{t('overview.welcome', { name: user.firstname && user.lastname 
          ? `${user.firstname} ${user.lastname}` 
          : user.name || t('nav.user') })}</small>
      </div>
      
      {/* Entfernt: Die 4 großen Kacheln (Mitarbeiter, Schichten, Dokumente, Berichte) da sie nicht funktionieren */}

      {/* Einsatzzeiten noch nicht bestätigt – der Weg fuer alle ohne WhatsApp */}
      {user.role !== 'admin' && planStatus && planStatus.needsConfirm && (
        <div className="alert alert-warning border-warning shadow-sm" style={{ borderLeftWidth: '6px' }}>
          <h4 className="alert-heading mb-2">⏰ Bitte bestätige deine Einsatzzeiten</h4>
          <p style={{ fontSize: '1.05rem' }}>
            Dein Dienstplan steht: <strong>{planStatus.shifts} Schicht{planStatus.shifts === 1 ? '' : 'en'}</strong>.
            Schau sie dir unter „Einsatz" an und bestätige kurz, dass alles passt.
          </p>
          <div className="d-flex flex-wrap gap-2">
            <button className="btn btn-primary" onClick={() => onGoTab && onGoTab('reports')}>
              <i className="bi bi-calendar-check me-1"></i>Einsatzzeiten ansehen
            </button>
            <button className="btn btn-success" onClick={() => replyPlan(false)}>Passt so – bestätigen</button>
            <button className="btn btn-outline-secondary" onClick={() => replyPlan(true)}>Es gibt ein Problem</button>
          </div>
        </div>
      )}

      {/* Großer Hinweis, solange der Dienstplan nicht final ist */}
      {user.role !== 'admin' && !planningFinal && (
        <div className="alert alert-warning border-warning shadow-sm" style={{ borderLeftWidth: '6px' }}>
          <h4 className="alert-heading mb-2">{t('overview.planningDraftTitle')}</h4>
          <p className="mb-0" style={{ fontSize: '1.05rem' }}>{t('overview.planningDraftText')}</p>
        </div>
      )}

      {/* Admin: offene Schichten, auf die sich jemand gemeldet hat */}
      {user.role === 'admin' && adminClaims.length > 0 && (
        <div className="card border-warning shadow-sm mt-3" style={{ borderLeftWidth: '6px' }}>
          <div className="card-header bg-white d-flex align-items-center justify-content-between">
            <h5 className="mb-0">🙋 Meldungen auf freie Schichten ({adminClaims.reduce((a, o) => a + o.claims.length, 0)})</h5>
            <small className="text-muted">✓ plant ein und schickt die WhatsApp · ✗ lehnt ab</small>
          </div>
          <div className="card-body pt-2">
            {adminClaims.map((o) => (
              <div key={o.id} className="py-2 border-bottom">
                <div className="fw-bold">
                  {new Date(o.date + 'T12:00').toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' })}
                  {' · '}{String(o.from).padStart(2, '0')}:00–{String(o.to === 25 ? 1 : o.to).padStart(2, '0')}:00
                  {' · '}{o.station}
                  {o.needed > 1 && <span className="badge bg-secondary ms-2">{o.needed} Plätze</span>}
                </div>
                {o.claims.map((c) => (
                  <div key={c.claim_id} className="d-flex align-items-center justify-content-between border rounded px-2 py-1 mt-1 bg-warning bg-opacity-10">
                    <div>
                      <strong>{c.name}</strong>{c.kasse ? ' 💰' : ''}
                      <small className="text-muted"> · bisher {c.shifts} Schichten{c.source === 'whatsapp' ? ' · 📱 WhatsApp' : ''}</small>
                    </div>
                    <div className="btn-group btn-group-sm">
                      <button className="btn btn-success" title="Einplanen und benachrichtigen" onClick={() => decideClaim(c, o, true)}>✓</button>
                      <button className="btn btn-outline-secondary" title="Ablehnen – mit Begründung"
                        onClick={() => setDeclineBox({ claim: c, offer: o, note: '' })}>✗</button>
                    </div>
                  </div>
                ))}
                {o.declined.length > 0 && (
                  <small className="text-muted d-block mt-1">✗ abgesagt: {o.declined.map((d) => d.name).join(', ')}</small>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {declineBox && (
        <ClaimDeclineModal key={declineBox.claim.claim_id} box={declineBox}
          onClose={() => setDeclineBox(null)}
          onConfirm={(note, send) => decideClaim(declineBox.claim, declineBox.offer, false, note, send)} />
      )}

      {/* Was ist aus meinen Anfragen geworden? Ohne diese Karte verschwindet eine
          Anfrage spurlos, sobald die Leitung entschieden hat – und im Zweifel
          steht jemand an einer Schicht, die er gar nicht bekommen hat. */}
      {user.role !== 'admin' && myClaims.length > 0 && (
        <div className="card shadow-sm mt-3">
          <div className="card-header bg-white">
            <h5 className="mb-0">🙋 Deine Anfragen auf freie Schichten</h5>
          </div>
          <div className="card-body pt-2">
            {myClaims.map((c) => {
              const zeit = `${String(c.from).padStart(2, '0')}:00–${String(c.to).padStart(2, '0')}:00`;
              const tag = new Date(c.date + 'T12:00').toLocaleDateString('de-DE',
                { weekday: 'short', day: '2-digit', month: '2-digit' });
              const st = c.status === 'assigned' && c.imPlan
                ? { farbe: 'success', text: '✅ Du hast sie – steht in deinem Dienstplan' }
                : c.status === 'pending'
                  ? { farbe: 'warning', text: '⏳ Angefragt – noch keine Zusage' }
                  : c.status === 'declined'
                    ? { farbe: 'secondary', text: '❌ Nicht bekommen' }
                    : c.status === 'withdrawn'
                      ? { farbe: 'secondary', text: '↩️ Zurückgezogen' }
                      : { farbe: 'secondary', text: '❌ Nicht bekommen' };
              return (
                <div key={c.id} className="d-flex flex-wrap align-items-center justify-content-between border-bottom py-2 gap-2">
                  <div>
                    <span className="fw-bold">{tag} · {zeit}</span>
                    <span className="text-muted ms-2">{c.station}</span>
                    {c.note && <div className="small text-muted">„{c.note}"</div>}
                  </div>
                  <span className={`badge bg-${st.farbe}`}>{st.text}</span>
                </div>
              );
            })}
            {myClaims.some((c) => c.status === 'pending') && (
              <div className="alert alert-warning mt-2 mb-0 py-2">
                <strong>Wichtig:</strong> „Angefragt" heißt noch <u>nicht</u>, dass du die Schicht hast.
                Komm bitte nur zu Schichten, die unter „Einsatz" in deinem Dienstplan stehen.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Schichtbörse: freie Schichten, auf die man sich melden kann */}
      {user.role !== 'admin' && offers.length > 0 && (
        <div className="card border-warning shadow-sm mt-3" style={{ borderLeftWidth: '6px' }}>
          <div className="card-header bg-white d-flex align-items-center justify-content-between">
            <h5 className="mb-0">🔥 Freie Schichten – wer möchte übernehmen?</h5>
            <small className="text-muted">Anfragen ist unverbindlich – erst die Leitung teilt ein.</small>
          </div>
          <div className="card-body pt-2">
            {/* Nach Tagen gruppiert – pro Tag eine Zeile mit den freien Zeitfenstern */}
            {Object.entries(offers.reduce((acc, o) => { (acc[o.date] = acc[o.date] || []).push(o); return acc; }, {}))
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([date, list]) => (
                <div key={date} className="py-2 border-bottom">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <div style={{ minWidth: 150 }}>
                      <span className="fw-bold" style={{ fontSize: '1.05rem' }}>
                        {new Date(date + 'T12:00').toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' })}
                      </span>
                      {list[0].anschluss && (
                        <div><small className="text-success">{list[0].anschluss}</small></div>
                      )}
                      {/* Hinweis, wenn man sich für sich überschneidende Zeiten gemeldet hat */}
                      {(() => {
                        const zu = list.filter((x) => x.claimed === 'pending');
                        const ueber = zu.some((a, i) => zu.some((b, j) => i !== j &&
                          Math.min(a.rawTo, b.rawTo) - Math.max(a.rawFrom, b.rawFrom) > 0));
                        return ueber ? <div><small className="text-primary">↔️ überschneidet sich – du bekommst höchstens eine davon</small></div> : null;
                      })()}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {list.sort((a, b) => a.from - b.from).map((o) => (
                        <div key={o.id} className="btn-group btn-group-sm" role="group"
                          title={`${o.station}${o.needed > 1 ? ` · ${o.needed} Plätze frei` : ''}`}>
                          <span className={`btn ${o.claimed === 'pending' ? 'btn-success' : o.claimed === 'declined' ? 'btn-secondary' : 'btn-light'} disabled`}
                            style={{ opacity: 1 }}>
                            {String(o.from).padStart(2, '0')}:00–{String(o.to).padStart(2, '0')}:00
                            <span className="ms-1 opacity-75" style={{ fontSize: '0.85em' }}>{o.station}</span>
                            {o.needed > 1 && <span className="badge bg-white text-dark ms-1">{o.needed}×</span>}
                          </span>
                          <button
                            className={`btn ${o.claimed === 'pending' ? 'btn-success' : 'btn-outline-success'}`}
                            title={o.claimed === 'pending' ? 'Angefragt – noch keine Zusage. Klicken zum Zurückziehen.' : 'Ich übernehme das gern'}
                            onClick={() => claimOffer(o.id, { withdraw: o.claimed === 'pending' })}
                          >✓</button>
                          <button
                            className={`btn ${o.claimed === 'declined' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                            title={o.claimed === 'declined' ? 'Du hast abgesagt – klicken zum Zurücknehmen' : 'Kann ich leider nicht'}
                            onClick={() => claimOffer(o.id, o.claimed === 'declined' ? { withdraw: true } : { decline: true })}
                          >✗</button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            <small className="text-muted d-block mt-2">
              ✓ = „übernehme ich gern" · ✗ = „kann ich nicht". Beides ist eine Anfrage, keine Zusage, und jederzeit änderbar –
              die Leitung teilt ein und meldet sich. Angezeigt wird nur, was sich nicht mit deinen eigenen Schichten überschneidet.
            </small>
          </div>
        </div>
      )}

      <div className="row mt-4">
        <div className="col-md-8">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white">
              <h5><i className="bi bi-clock-fill me-2 text-primary"></i>
                {user.role !== 'admin' ? t('overview.myShiftsToday', { date: getTodaysDate() }) : t('overview.currentShifts')}
              </h5>
            </div>
            <div className="card-body">
              {user.role !== 'admin' ? (
                loadingShifts ? (
                  <div className="text-center py-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">{t('overview.loading')}</span>
                    </div>
                    <p className="mt-2 text-muted">{t('overview.loadingShifts')}</p>
                  </div>
                ) : todayShifts.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>{t('overview.time')}</th>
                          <th>{t('overview.station')}</th>
                          <th>{t('overview.status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {todayShifts.map((shift, index) => (
                          <tr key={index}>
                            <td>
                              <strong>{formatTime(shift.start_time)} - {formatTime(shift.end_time)}</strong>
                            </td>
                            <td>
                              <span className="badge bg-light text-dark">{getStationName(shift.station_id)}</span>
                            </td>
                            <td>
                              <span className="badge bg-success">
                                <i className="bi bi-calendar-check me-1"></i>
                                {t('overview.planned')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="bi bi-calendar-x text-muted" style={{fontSize: '3rem'}}></i>
                    <p className="mt-2 text-muted">{t('overview.noShifts')}</p>
                    <small className="text-muted">{t('overview.freeDay')}</small>
                  </div>
                )
              ) : (
                <p className="text-muted">{t('overview.adminPlaceholder')}</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="col-md-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white d-flex align-items-center justify-content-between">
              <h5 className="mb-0"><i className="bi bi-bell-fill me-2 text-warning"></i>{t('overview.notifications')}</h5>
              {notifications.length > 0 && (
                <span className="badge bg-danger rounded-pill">{notifications.length}</span>
              )}
            </div>
            <div className="card-body">
              {notifications.length === 0 ? (
                <p className="text-muted mb-0">{t('overview.noNotifications')}</p>
              ) : (
                <ul className="list-group list-group-flush">
                  {notifications.map((n, i) => (
                    <li key={i} className="list-group-item d-flex align-items-start px-0 border-0">
                      <i className={`bi bi-${n.icon || 'info-circle'} me-2 text-${n.level || 'secondary'}`} style={{ fontSize: '1.1rem' }}></i>
                      <span>{n.text}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Arbeitsanweisungen Sektion */}
      <div className="row mt-4">
        <div className="col-12">
          <WorkInstructions user={user} />
        </div>
      </div>

      {/* Allgemeine Dokumente Sektion */}
      <div className="row mt-4">
        <div className="col-12">
          <GeneralDocuments user={user} />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
